// 🔧 ranked_choice_helpers.js
// ✅ Purpose: Calculate ranked choice / instant-runoff voting results
// ✅ Features:
// - Accepts ranked choice vote documents
// - Counts first-choice votes each round
// - Eliminates the lowest candidate each round
// - Transfers ballots to the next available ranked choice
// - Stops when a candidate has majority support
// - Returns winner, rounds, exhausted ballots, and final standings
// - Keeps ranked choice math out of React page components

// ✅ Safely normalize ranking arrays into ordered cid values
const normalizeRankings = (rankings = []) => {
  const safeRankings = Array.isArray(rankings) ? rankings : [];

  return safeRankings
    .filter((item) => item?.cid)
    .sort((a, b) => Number(a.rank || 999) - Number(b.rank || 999))
    .map((item) => item.cid);
};

// ✅ Finds the first ranked cid that has not been eliminated
const getActiveChoice = (rankedCids, eliminatedCids) => {
  return rankedCids.find((cid) => !eliminatedCids.has(cid)) || null;
};

// ✅ Main ranked choice calculator
export const calculateRankedChoiceResults = ({
  votes = [],
  candidateIds = [],
}) => {
  const activeCandidateIds = candidateIds.filter(Boolean);
  const eliminatedCids = new Set();
  const rounds = [];

  const ballots = votes
    .map((vote) => ({
      voteId: vote.id || null,
      uid: vote.uid || null,
      rankings: normalizeRankings(vote.rankings),
    }))
    .filter((ballot) => ballot.rankings.length > 0);

  let winnerCid = null;
  let roundNumber = 1;

  while (!winnerCid) {
    const counts = {};
    let exhaustedBallots = 0;

    activeCandidateIds.forEach((cid) => {
      if (!eliminatedCids.has(cid)) {
        counts[cid] = 0;
      }
    });

    ballots.forEach((ballot) => {
      const activeChoice = getActiveChoice(ballot.rankings, eliminatedCids);

      if (activeChoice && counts[activeChoice] !== undefined) {
        counts[activeChoice] += 1;
      } else {
        exhaustedBallots += 1;
      }
    });

    const activeEntries = Object.entries(counts);
    const activeVoteTotal = activeEntries.reduce((sum, [, count]) => sum + count, 0);

    const majorityNeeded = Math.floor(activeVoteTotal / 2) + 1;

    const winnerEntry = activeEntries.find(([, count]) => count >= majorityNeeded);

    if (winnerEntry) {
      winnerCid = winnerEntry[0];

      rounds.push({
        round: roundNumber,
        counts,
        activeVoteTotal,
        exhaustedBallots,
        majorityNeeded,
        winnerCid,
        eliminatedCid: null,
      });

      break;
    }

    if (activeEntries.length <= 1) {
      winnerCid = activeEntries[0]?.[0] || null;

      rounds.push({
        round: roundNumber,
        counts,
        activeVoteTotal,
        exhaustedBallots,
        majorityNeeded,
        winnerCid,
        eliminatedCid: null,
      });

      break;
    }

    // =====================================================
    // Safety Check
    //
    // If every remaining candidate is tied,
    // there is nobody left to eliminate.
    //
    // Example:
    //
    // A = 10
    // B = 10
    // C = 10
    //
    // In this case we stop and declare the
    // remaining candidates tied.
    // =====================================================

    if (
    activeEntries.every(
        ([, count]) => count === activeEntries[0][1]
    )
    ) {
    rounds.push({
        round: roundNumber,
        counts,
        activeVoteTotal,
        exhaustedBallots,
        majorityNeeded,
        winnerCid: null,
        tiedCids: activeEntries.map(([cid]) => cid),
    });

    break;
    }

    // Example:
    //
    // Candidate A = 50
    // Candidate B = 25
    // Candidate C = 10
    // Candidate D = 10
    //
    // Result:
    //
    // C and D are BOTH eliminated.
    //
    // Why?
    //
    // - Deterministic
    // - Fully auditable
    // - No random tie breakers
    // - No cid/alphabetical bias
    //
    // After elimination, ballots that selected C or D
    // automatically transfer to their next ranked choice
    // during the next counting round.
    //
    // =====================================================

    // Find the lowest vote count still active
    const lowestVoteCount = Math.min(
    ...activeEntries.map(([, count]) => count)
    );

    // Find every candidate tied for the lowest count
    const eliminatedRoundCids = activeEntries
        .filter(([, count]) => count === lowestVoteCount)
        .map(([cid]) => cid);

    // Eliminate all tied lowest candidates
    eliminatedRoundCids.forEach((cid) => {
        eliminatedCids.add(cid);
    });

    rounds.push({
    round: roundNumber,
    counts,
    activeVoteTotal,
    exhaustedBallots,
    majorityNeeded,
    winnerCid: null,
    eliminatedCids: eliminatedRoundCids,
    });

    roundNumber += 1;
  }

  const finalRound = rounds[rounds.length - 1];

  const finalStandings = Object.entries(finalRound?.counts || {})
    .map(([cid, count]) => ({
      cid,
      count,
      eliminated: eliminatedCids.has(cid),
      winner: cid === winnerCid,
    }))
    .sort((a, b) => b.count - a.count);

  // =====================================================
  // 🏆 Leaderboard Placement Calculation
  //
  // Ranked Choice elections determine a winner through
  // successive elimination rounds.
  //
  // For Let'sGoVote leaderboards we also want:
  //
  // 1st Place
  // 2nd Place
  // 3rd Place
  // 4th Place
  // 5th Place
  //
  // We derive this from elimination order:
  //
  // Winner = 1st
  // Final Runner-Up = 2nd
  // Last Eliminated = 3rd
  // Previous Eliminated = 4th
  // etc.
  // =====================================================

  const eliminationOrder = [];

  rounds.forEach((round) => {
    if (Array.isArray(round.eliminatedCids)) {
      eliminationOrder.push(...round.eliminatedCids);
    }
  });

  const leaderboardPlacements = [];

  // ✅ Winner
  if (winnerCid) {
    leaderboardPlacements.push({
      place: 1,
      cid: winnerCid,
    });
  }

  // ✅ Determine runner-up from final round
  const runnerUpCid = finalStandings
    .filter((entry) => entry.cid !== winnerCid)
    .sort((a, b) => b.count - a.count)[0]?.cid;

  if (runnerUpCid) {
    leaderboardPlacements.push({
      place: 2,
      cid: runnerUpCid,
    });
  }

  // ✅ Remaining placements come from reverse elimination order
  const remainingPlacements = eliminationOrder
    .filter((cid) => cid !== winnerCid && cid !== runnerUpCid)
    .reverse();

  remainingPlacements.forEach((cid, index) => {
    leaderboardPlacements.push({
      place: index + 3,
      cid,
    });
  });

    return {
    winnerCid,
    rounds,
    finalStandings,
    leaderboardPlacements,
    winningRound: finalRound,
    exhaustedBallots: finalRound?.exhaustedBallots || 0,
    totalBallots: ballots.length,
    };
};